import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import {
  Activity,
  AlertTriangle,
  ArrowRight,
  Clock,
  Loader2,
  RefreshCw,
} from "lucide-react";
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { useMeta } from "@/stores/meta";
import { runsApi } from "@/api/runs";
import { metricsApi } from "@/api/metrics";
import { appsApi } from "@/api/apps";
import { functionsApi } from "@/api/functions";
import { StatCard } from "@/components/stat-card";
import { StatusBadge } from "@/components/status-badge";
import { DispatchMark } from "@/components/brand/dispatch-mark";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/cn";
import { formatRelative, formatRunDuration } from "@/lib/format";
import { POLL, isRunActive } from "@/lib/poll";
import type { Metrics } from "@/api/types";

export const Route = createFileRoute("/_authed/")({
  component: HomePage,
});

type WindowKey = "24h" | "7d" | "30d";
const WINDOWS: { value: WindowKey; label: string; hint: string; unit: "hour" | "day"; per: string }[] = [
  { value: "24h", label: "24h", hint: "last 24h", unit: "hour", per: "hour" },
  { value: "7d", label: "7d", hint: "last 7 days", unit: "day", per: "day" },
  { value: "30d", label: "30d", hint: "last 30 days", unit: "day", per: "day" },
];

function HomePage() {
  // Time range for the KPIs + throughput chart. 24h by default; 7d/30d let you
  // look further back (hourly buckets for 24h, daily for the wider ranges).
  const [range, setRange] = useState<WindowKey>("24h");
  const win = WINDOWS.find((w) => w.value === range) ?? WINDOWS[0];

  // Real environment-wide metrics over the selected window, computed in SQL and
  // EXCLUDING cron runs — so the KPIs/chart reflect event-driven work and never
  // freeze at the old 200-row page ceiling.
  const metricsQ = useQuery({
    queryKey: ["overview-metrics", range],
    queryFn: () => metricsApi.overview(range),
    refetchInterval: POLL.idle, // windowed — coarse refresh is plenty.
  });

  // A short page of the newest event-driven runs for the "Latest runs" table
  // (event-focused, matching the KPI — cron lives on the Schedules page).
  const latestQ = useQuery({
    queryKey: ["latest-runs"],
    queryFn: () => runsApi.list({ trigger: "event", limit: 8 }),
    refetchInterval: (query) => {
      const data = query.state.data;
      if (!data) return POLL.active;
      const hasActive = data.data.some((r) => isRunActive(r.status));
      return hasActive ? POLL.active : POLL.idle;
    },
  });

  // Fresh-install detection: no apps registered and no functions synced yet.
  const appsQ = useQuery({ queryKey: ["apps"], queryFn: () => appsApi.list() });
  const functionsQ = useQuery({ queryKey: ["functions"], queryFn: () => functionsApi.list() });
  // Only treat it as a fresh install when BOTH queries have succeeded and
  // returned empty — an errored query must not fall through to onboarding.
  const freshInstall =
    !appsQ.isLoading && !functionsQ.isLoading &&
    !appsQ.isError && !functionsQ.isError &&
    (appsQ.data?.data.length ?? 0) === 0 && (functionsQ.data?.data.length ?? 0) === 0;

  const metrics = metricsQ.data;
  const stats = {
    total:   metrics?.total ?? 0,
    running: metrics?.running ?? 0,
    pending: (metrics?.pending ?? 0) + (metrics?.waiting ?? 0),
    failed:  metrics?.failed ?? 0,
  };

  const buckets = useMemo(() => mapBuckets(metrics?.buckets ?? [], win.unit), [metrics, win.unit]);
  const totalSpark = buckets.map((b) => b.total);
  const failSpark = buckets.map((b) => b.failed);
  const latest = latestQ.data?.data ?? [];
  // Include apps/functions in the loading gate so the normal Overview shows
  // skeletons (not zeroed cards) until fresh-install detection settles — avoids
  // a flash of the empty dashboard before the onboarding panel swaps in.
  const loading = metricsQ.isLoading || latestQ.isLoading || appsQ.isLoading || functionsQ.isLoading;

  return (
    <div className="space-y-6">
      <PageHeader
        isFetching={metricsQ.isFetching || latestQ.isFetching}
        updatedAt={metricsQ.dataUpdatedAt}
        onRefresh={() => { metricsQ.refetch(); latestQ.refetch(); }}
      />

      {freshInstall ? (
        <OnboardingPanel />
      ) : (
        <>
          {/* KPI row */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {loading ? (
              Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-[104px] rounded-xl" />)
            ) : (
              <>
                <StatCard label="Runs" value={stats.total} accent="brand" hint={win.hint}
                  icon={<Activity size={15} />} spark={totalSpark} />
                <StatCard label="Running" value={stats.running} accent="run" live
                  icon={<Loader2 size={15} className={stats.running > 0 ? "animate-spin" : ""} />} />
                <StatCard label="Pending" value={stats.pending} accent="wait" live
                  icon={<Clock size={15} />} />
                <StatCard label="Failed" value={stats.failed} accent="fail"
                  icon={<AlertTriangle size={15} />} spark={failSpark} />
              </>
            )}
          </div>

          {/* throughput chart */}
          <Card className="p-5">
            <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
              <div>
                <h2 className="font-display text-[15px] font-semibold tracking-tight">Throughput</h2>
                <p className="text-xs text-muted-foreground">Runs per {win.per} · {win.hint}</p>
              </div>
              <div className="flex items-center gap-3">
                <ChartLegend />
                <WindowChips value={range} onChange={setRange} />
              </div>
            </div>
            <div className="h-56">
              {loading ? (
                <Skeleton className="h-full w-full rounded-lg" />
              ) : (
                <ThroughputChart data={buckets} />
              )}
            </div>
          </Card>

          {/* latest runs */}
          <Card className="overflow-hidden">
            <div className="flex items-center justify-between border-b px-5 py-3.5">
              <h2 className="font-display text-[15px] font-semibold tracking-tight">Latest runs</h2>
              <Link to="/runs" className="flex items-center gap-1 text-xs font-medium text-brand hover:underline">
                View all <ArrowRight size={13} />
              </Link>
            </div>
            {loading ? (
              <div className="space-y-2 p-5">
                {Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-9 w-full" />)}
              </div>
            ) : latest.length === 0 ? (
              <EmptyRuns />
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="hover:bg-transparent">
                    <TableHead className="pl-5 font-mono text-[11px] uppercase tracking-wider">Status</TableHead>
                    <TableHead className="font-mono text-[11px] uppercase tracking-wider">Function</TableHead>
                    <TableHead className="font-mono text-[11px] uppercase tracking-wider">Event</TableHead>
                    <TableHead className="text-right font-mono text-[11px] uppercase tracking-wider">Duration</TableHead>
                    <TableHead className="pr-5 text-right font-mono text-[11px] uppercase tracking-wider">Started</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {latest.map((r) => (
                    <TableRow key={r.id} className="group cursor-pointer">
                      <TableCell className="pl-5"><StatusBadge status={r.status} /></TableCell>
                      <TableCell>
                        <Link to="/runs/$id" params={{ id: r.id }}
                          className="font-mono text-[13px] font-medium text-foreground group-hover:text-brand">
                          {r.function_slug}
                        </Link>
                      </TableCell>
                      <TableCell className="font-mono text-[13px] text-muted-foreground">{r.event_name ?? "—"}</TableCell>
                      <TableCell className="tabular text-right font-mono text-[13px] text-muted-foreground">
                        {formatRunDuration(r.started_at, r.completed_at)}
                      </TableCell>
                      <TableCell className="tabular pr-5 text-right font-mono text-[13px] text-muted-foreground">
                        {formatRelative(r.started_at)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </Card>
        </>
      )}
    </div>
  );
}

function PageHeader({ isFetching, updatedAt, onRefresh }: { isFetching: boolean; updatedAt: number; onRefresh: () => void }) {
  const meta = useMeta((s) => s.meta);
  // Label the running tier, matching the sidebar badge (cloud > dev binary > env).
  const eyebrow = meta ? (meta.cloud ? "Cloud" : meta.devServer ? "Dev server" : meta.env) : "";
  return (
    <div className="flex items-end justify-between gap-4">
      <div>
        <div className="mb-1 font-mono text-[11px] font-medium uppercase tracking-[0.16em] text-muted-foreground">
          {eyebrow}
        </div>
        <h1 className="font-display text-2xl font-semibold tracking-tight">Overview</h1>
      </div>
      <button
        onClick={onRefresh}
        className="flex items-center gap-1.5 rounded-md border bg-card px-2.5 py-1.5 text-xs text-muted-foreground transition-colors hover:text-foreground"
      >
        <RefreshCw size={13} className={isFetching ? "animate-spin" : ""} />
        {updatedAt ? `updated ${formatRelative(new Date(updatedAt).toISOString())}` : "refresh"}
      </button>
    </div>
  );
}

function ChartLegend() {
  return (
    <div className="flex items-center gap-4 text-xs text-muted-foreground">
      <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-ok" /> completed</span>
      <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-fail" /> failed</span>
    </div>
  );
}

function ThroughputChart({ data }: { data: Bucket[] }) {
  return (
    <ResponsiveContainer width="100%" height="100%">
      <AreaChart data={data} margin={{ top: 4, right: 20, left: 0, bottom: 0 }}>
        <defs>
          <linearGradient id="grad-ok" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="var(--st-ok)" stopOpacity={0.35} />
            <stop offset="100%" stopColor="var(--st-ok)" stopOpacity={0} />
          </linearGradient>
          <linearGradient id="grad-fail" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="var(--st-fail)" stopOpacity={0.3} />
            <stop offset="100%" stopColor="var(--st-fail)" stopOpacity={0} />
          </linearGradient>
        </defs>
        <CartesianGrid vertical={false} stroke="hsl(var(--border))" strokeDasharray="3 3" opacity={0.5} />
        <XAxis
          dataKey="label" tickLine={false} axisLine={false}
          interval={Math.max(0, Math.ceil(data.length / 8) - 1)}
          tick={{ fontSize: 11, fontFamily: "var(--font-mono)", fill: "hsl(var(--muted-foreground))" }}
        />
        <YAxis
          tickLine={false} axisLine={false} allowDecimals={false} width={34}
          tick={{ fontSize: 11, fontFamily: "var(--font-mono)", fill: "hsl(var(--muted-foreground))" }}
        />
        <Tooltip
          contentStyle={{
            fontSize: 12, fontFamily: "var(--font-mono)",
            backgroundColor: "hsl(var(--popover))",
            border: "1px solid hsl(var(--border))",
            borderRadius: 8, boxShadow: "0 8px 24px rgb(0 0 0 / 0.35)",
            color: "hsl(var(--popover-foreground))",
          }}
          labelStyle={{ color: "hsl(var(--muted-foreground))", marginBottom: 4 }}
          cursor={{ stroke: "hsl(var(--muted-foreground))", strokeWidth: 1, strokeDasharray: "3 3" }}
        />
        {/* not stacked — each series shows its own true count per hour */}
        <Area type="monotone" dataKey="completed" stroke="var(--st-ok)" strokeWidth={2} fill="url(#grad-ok)" />
        <Area type="monotone" dataKey="failed" stroke="var(--st-fail)" strokeWidth={2} fill="url(#grad-fail)" />
      </AreaChart>
    </ResponsiveContainer>
  );
}

/** Fresh-install panel: shown on Overview when no apps and no functions exist yet. */
function OnboardingPanel() {
  return (
    <Card className="flex flex-col items-center gap-4 px-6 py-14 text-center">
      <DispatchMark animated className="h-12 w-auto text-brand" />
      <div className="max-w-md space-y-1.5">
        <h2 className="font-display text-lg font-semibold tracking-tight">
          Connect your first app to start dispatching functions
        </h2>
        <p className="text-sm text-muted-foreground">
          Register an app with your SDK endpoint, sync its manifest, and deleggate will start
          routing matching events to your functions.
        </p>
      </div>
      <div className="flex items-center gap-3">
        <Button asChild className="bg-brand text-primary-foreground hover:bg-brand/90">
          <Link to="/apps">
            Add an app <ArrowRight size={14} />
          </Link>
        </Button>
        <Button asChild variant="outline">
          <a href="/docs/quickstart/">Read the quickstart</a>
        </Button>
      </div>
    </Card>
  );
}

function EmptyRuns() {
  return (
    <div className="flex flex-col items-center justify-center gap-4 py-16 text-center">
      <DispatchMark animated className="h-12 w-auto text-muted-foreground/40" />
      <div>
        <p className="text-sm font-medium">No runs yet</p>
        <p className="mt-1 text-xs text-muted-foreground">
          Send an event and your functions will start dispatching here.
        </p>
      </div>
    </div>
  );
}

interface Bucket { hour: number; label: string; total: number; completed: number; failed: number }

/** Map the backend's metric buckets to the chart/sparkline shape. Hourly buckets
 * are labelled "HH:00"; daily buckets "MM/DD". */
function mapBuckets(bs: Metrics["buckets"], unit: "hour" | "day"): Bucket[] {
  return bs.map((b) => ({
    hour: new Date(b.hour_start).getTime(),
    label: unit === "day"
      ? b.hour_start.slice(5, 7) + "/" + b.hour_start.slice(8, 10)
      : b.hour_start.slice(11, 13) + ":00",
    total: b.total,
    completed: b.completed,
    failed: b.failed,
  }));
}

/** Segmented time-range selector for the throughput chart. */
function WindowChips({ value, onChange }: { value: WindowKey; onChange: (w: WindowKey) => void }) {
  return (
    <div className="inline-flex items-center gap-0.5 rounded-lg border bg-muted/40 p-0.5">
      {WINDOWS.map((w) => (
        <button
          key={w.value}
          type="button"
          onClick={() => onChange(w.value)}
          aria-pressed={value === w.value}
          className={cn(
            "rounded-md px-2.5 py-1 text-xs font-medium transition-colors",
            value === w.value
              ? "bg-brand text-primary-foreground"
              : "text-muted-foreground hover:text-foreground",
          )}
        >
          {w.label}
        </button>
      ))}
    </div>
  );
}
